import { Link } from 'react-router-dom'
import { useState } from 'react'
import type { Product } from '../lib/products'
import { calcCommission, getCategory } from '../lib/products'
import { useI18n } from '../lib/i18n'

/**
 * Light marketplace card (Webflow-template presentation language on the
 * site's light shell): real screenshot of the working demo on top, name +
 * monthly price up front. Screenshots: /previews/<slug>.jpg
 * (scripts/gen-previews.mjs).
 */
export default function ProductCard({ product }: { product: Product }) {
  const { t } = useI18n()
  const c = calcCommission(product.plans.pro.price)
  const live = !!product.demoUrl
  const cat = getCategory(product.category)
  const [imgOk, setImgOk] = useState(true)

  return (
    <Link
      to={`/catalog/${product.slug}`}
      className="group block overflow-hidden rounded-2xl border border-[#dfe5ef] bg-white shadow-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-300/60 hover:border-[#ffb21f]/70 transition-all duration-200"
    >
      {/* Preview — a real screenshot of the working product */}
      <div className="relative aspect-[8/5] overflow-hidden bg-[#eef2f8] border-b border-[#eaeef5]">
        {live && imgOk ? (
          <img
            src={`/previews/${product.slug}.jpg`}
            alt={`${product.name} live demo preview`}
            loading="lazy"
            onError={() => setImgOk(false)}
            className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#f7f9fd] to-[#eef2f8]">
            <span className="text-6xl" aria-hidden>{product.icon}</span>
          </div>
        )}
        {live ? (
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-black text-emerald-700 border border-emerald-200 shadow-sm backdrop-blur-sm">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t('Live demo')}
          </span>
        ) : (
          <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-black text-amber-700 border border-amber-200 shadow-sm backdrop-blur-sm">
            {t('Demo ready')}
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-black text-[#071023] leading-snug group-hover:text-[#b97900] transition-colors">
            {product.name}
          </h3>
          <span className="shrink-0 text-right">
            <span className="block text-base font-black text-[#071023]">€{product.plans.starter.price}<span className="text-xs font-semibold text-[#8b96ab]">{t('/mo')}</span></span>
          </span>
        </div>
        <p className="mt-1 text-sm font-medium text-[#65728d] line-clamp-2">{t(product.tagline)}</p>
        <div className="mt-3 pt-3 border-t border-[#eaeef5] flex items-center justify-between text-xs font-bold">
          <span className="text-[#8b96ab]">{cat?.icon} {t(cat?.name ?? product.category)}</span>
          <span className="text-emerald-600">{t('Partner earns')} €{c.total6m}</span>
        </div>
      </div>
    </Link>
  )
}
